import { Button, Flex } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BsExclamationTriangleFill, BsEyeFill, BsEyeSlashFill } from 'react-icons/bs';
import { TbArrowRight } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { TokenPagination } from '../../components/TokenPagination';
import Banner from '../../custom-components/Banner';
import ConfirmModal from '../../custom-components/ConfirmModal';
import CreateCompaniesModal from '../../custom-components/CreateCompaniesModal';
import { ExportReviewGrid, FieldMapping, ReviewChildRecord, ReviewRecord } from '../../custom-components/ExportReviewGrid';
import LoadingBars from '../../custom-components/LoadingBars';
import { RecordExplanationSlideout } from '../../custom-components/RecordExplanationSlideout';
import { REVIEW_STEPS, ReviewProgressSteps, ReviewStepKey } from '../../custom-components/ReviewProgressSteps';
import { usePermission } from '../../hooks/usePermission';
import { enumLabel, toastV4Error, v4Error } from '../../services/edulead-v4';
import { eduleadV4 } from '../../services/edulead-v4-client';
import { subscribeToExportReview } from '../../services/pusher.service';
import { ExportContext } from '../ExportDetail';
import { GlobalContext } from '../../global-context';
import { decisionOf, ReviewRow, reviewRowOf } from './exportShapes';

/** What the account-billing line shows; null when the team's limit is unlimited. */
interface UnlockedStats {
  used: number;
  limit: number;
  wouldUnlock: number;
  alreadyUnlocked: number;
  hasLimit: boolean;
}

/** GetExport's billable institutions as the line reads them; unset is no limit. */
function unlockedStatsOf(b: Edulead.EduleadV4ExportBillableInstitutions | undefined): UnlockedStats | null {
  if (!b) return null;
  return {
    used: b.used ?? 0,
    limit: b.limit ?? 0,
    wouldUnlock: b.would_unlock ?? 0,
    alreadyUnlocked: b.already_unlocked ?? 0,
    hasLimit: true,
  };
}

export function ExportStepReview() {
  const exportContext = useContext(ExportContext);
  const globalContext = useContext(GlobalContext);
  const navigate = useNavigate();
  const isAccountBasedBilling = globalContext?.authInfo?.team?.account_based_billing ?? false;
  const [currentStep, setCurrentStep] = useState<ReviewStepKey>(ReviewStepKey.FETCHING_DATA);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ReviewRow[] | null>(null);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  // pageTokens[i] loads page i + 1; the last is the page shown
  const [pageTokens, setPageTokens] = useState<string[]>(['']);
  const [nextPageToken, setNextPageToken] = useState('');
  const [pageSize] = useState(100);
  const pageRequest = useRef(0);
  const [selectedRecord, setSelectedRecord] = useState<{ record: ReviewRecord | ReviewChildRecord; recordType: string; parentIdCrm?: string } | null>(null);
  // Kept separate from selectedRecord so marks survive closing the slideout. Several rows
  // can be marked at once; clicking a marked row again clears it.
  const [highlightedRecordIds, setHighlightedRecordIds] = useState<Set<string>>(new Set());

  const toggleRowHighlight = (idSource: string) => {
    setHighlightedRecordIds(prev => {
      const next = new Set(prev);
      if (!next.delete(idSource)) next.add(idSource);
      return next;
    });
  };
  const [showAssociations, setShowAssociations] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [showChanges, setShowChanges] = useState(true);
  const [isStartingExport, setIsStartingExport] = useState(false);
  const [takeCount, setTakeCount] = useState(0);
  const [showCreateCompaniesModal, setShowCreateCompaniesModal] = useState(false);
  const [isRetryingReview, setIsRetryingReview] = useState(false);
  const [showRetryConfirmModal, setShowRetryConfirmModal] = useState(false);
  const [isCheckingCompaniesExport, setIsCheckingCompaniesExport] = useState(false);
  const [unlockedStats, setUnlockedStats] = useState<UnlockedStats | null>(null);

  const exportId = exportContext?.exportId;
  const exportStatus = exportContext?.exportData?.status;
  const team = { team: exportContext?.teamId };

  // Reset saving state on mount (in case previous step navigated while saving)
  useEffect(() => {
    exportContext?.setIsSaving(false);
  }, []);
  const emailValidationProvider = enumLabel(exportContext?.exportData?.options?.email_provider, 'EXPORT_EMAIL_PROVIDER_');
  const hasEmailValidation = emailValidationProvider && emailValidationProvider !== 'NONE';
  const fieldMappings = exportContext?.fieldMappings ?? [];

  // Determine if we're in progress mode based on export status
  const isReviewInProgress = exportStatus === 'REVIEW_PROGRESS';
  const isReady = exportStatus === 'REVIEW';
  const isFailed = exportStatus === 'FAILURE';
  const isExportInProgress = exportStatus === 'EXPORT_PROGRESS' || exportStatus === 'IN_PROGRESS';
  const isExportCompleted = exportStatus === 'COMPLETED' || exportStatus === 'SUCCESS' || exportStatus === 'PARTIAL_SUCCESS';

  // Show grid in read-only mode when export is in progress, completed, failed, or context is read-only (boss view)
  const contextReadOnly = exportContext?.readOnly ?? false;
  const showReadOnlyGrid = isExportInProgress || isExportCompleted || isFailed || contextReadOnly;

  // Filter steps based on export configuration
  const exportTarget = exportContext?.exportData?.target;
  const isCrmTarget = exportTarget === 'HUBSPOT' || exportTarget === 'SALESFORCE';
  const isSalesforceLead = exportContext?.exportData?.options?.salesforce_object === 'EXPORT_SALESFORCE_OBJECT_LEAD';
  const isContact = exportContext?.exportData?.entity === 'CONTACT';

  // Permission checks for continue/execute button
  const { hasPermission } = usePermission();
  const canExportToCrm = isContact
    ? hasPermission('export-people-crm:execute')
    : hasPermission('export-institutions-crm:execute');
  const canExportToFile = isContact
    ? hasPermission('export-people-file:execute')
    : hasPermission('export-institutions-file:execute');
  const canExecuteExport = isCrmTarget ? canExportToCrm : canExportToFile;

  // System field mappings - computed fields that aren't part of user field mappings
  const systemFieldMappings: FieldMapping[] = useMemo(() => {
    // Salesforce Lead exports have a Company field computed from institution
    if (exportTarget === 'SALESFORCE' && isSalesforceLead) {
      return [{ source: 'Company', target: 'Company', label: 'Company', visible: true, order: 0 }];
    }
    return [];
  }, [exportTarget, isSalesforceLead]);

  // Loads the page whose token is last in `tokens`, with the export's counts (the take count and
  // the account-billing line), as the page and its stats came together before. A response to a
  // superseded request is dropped.
  const loadPage = async (tokens: string[]) => {
    if (!exportId) return;
    const request = ++pageRequest.current;
    setIsLoadingRecords(true);
    try {
      const [page, { export: current }] = await Promise.all([
        eduleadV4.exports.listReviewRecords(
          { export_id: exportId, page_size: pageSize, page_token: tokens[tokens.length - 1] || undefined },
          team
        ),
        eduleadV4.exports.get({ id: exportId }, team),
      ]);
      if (request !== pageRequest.current) return;
      setPageTokens(tokens);
      setRows(page.data.map(reviewRowOf));
      setNextPageToken(page.response.next_page_token ?? '');
      setTakeCount(current?.review?.take ?? 0);
      if (isContact && isAccountBasedBilling) setUnlockedStats(unlockedStatsOf(current?.billable_institutions));
    } catch (err) {
      if (request !== pageRequest.current) return;
      console.error('Error fetching review records:', err);
      setError('Failed to load review records');
    } finally {
      if (request === pageRequest.current) setIsLoadingRecords(false);
    }
  };

  // Fetch review records when status is REVIEW or export is in progress/completed/failed
  const shouldFetchRecords = isReady || showReadOnlyGrid;
  useEffect(() => {
    if (!exportId || !shouldFetchRecords) return;
    loadPage(['']);
  }, [exportId, shouldFetchRecords, pageSize]);

  // Refresh the account-billing line after a decision (CONTACT exports, account-based billing)
  const fetchUnlockedStats = async () => {
    if (!exportId || !isContact || !isAccountBasedBilling) {
      return;
    }
    try {
      const { export: current } = await eduleadV4.exports.get({ id: exportId }, team);
      setUnlockedStats(unlockedStatsOf(current?.billable_institutions));
    } catch (err) {
      console.error('Error fetching unlocked institutions stats:', err);
    }
  };

  // Initialize currentStep from export data on load
  useEffect(() => {
    const step = exportContext?.exportData?.step;
    if (step) {
      setCurrentStep(step as ReviewStepKey);
    }
  }, [exportContext?.exportData?.step]);

  // Subscribe to Pusher events only when status is REVIEW_PROGRESS
  useEffect(() => {
    if (!exportId || !isReviewInProgress) return;

    console.log('[ExportStepReview] Subscribing to Pusher channel for export:', exportId);

    const unsubscribe = subscribeToExportReview(exportId, {
      onStep: (data) => {
        console.log('[ExportStepReview] Step:', data);
        setCurrentStep(data.step as ReviewStepKey);
      },
      onReady: () => {
        console.log('[ExportStepReview] Ready - refreshing export data');
        exportContext?.refreshExport();
      },
      onFailed: () => {
        console.log('[ExportStepReview] Failed');
        setError('Review preparation failed. Please try again.');
        exportContext?.refreshExport();
      },
    });

    return () => {
      console.log('[ExportStepReview] Unsubscribing from Pusher channel');
      unsubscribe();
    };
  }, [exportId, isReviewInProgress]);

  if (!exportContext) {
    return (
      <div className="content content-1200">
        <div className="export-step">
          <LoadingBars numberOfRows={5} />
        </div>
      </div>
    );
  }

  const handleContinue = async () => {
    if (!exportId) return;

    setIsStartingExport(true);
    setError(null);

    try {
      // Call API to start the export execution
      await eduleadV4.exports.approve({ id: exportId }, team);
      // Refresh export data to get updated status before navigating
      await exportContext?.refreshExport();
      // Navigate to export step to show progress
      navigate(`/exports/${exportId}/export`);
    } catch (err) {
      setError(v4Error(err).message || 'Failed to start export');
      toastV4Error(err, 'Failed to start export');
      setIsStartingExport(false);
    }
  };

  // Terminal statuses for exports
  const TERMINAL_STATUSES = ['COMPLETED', 'SUCCESS', 'PARTIAL_SUCCESS', 'FAILURE', 'REJECTED', 'CANCELLED'];

  // Handle retry review button click - check companies export status first
  const handleRetryReviewClick = async () => {
    const companiesExportId = exportContext?.exportData?.companies_export_id;
    if (!companiesExportId) return;

    setIsCheckingCompaniesExport(true);
    setError(null);

    try {
      const { export: companiesExport } = await eduleadV4.exports.get({ id: companiesExportId }, team);
      const companiesExportStatus = enumLabel(companiesExport?.state, 'EXPORT_STATE_');
      console.log('[ExportStepReview] Companies export status:', companiesExportStatus);
      const isTerminal = TERMINAL_STATUSES.includes(companiesExportStatus);

      if (isTerminal) {
        // Companies export is done, proceed directly
        await executeRetryReview();
      } else {
        // Companies export is still in progress, show confirmation
        setShowRetryConfirmModal(true);
      }
    } catch (err) {
      console.error('Error checking companies export:', err);
      // If we can't fetch the companies export, show confirmation to be safe
      setShowRetryConfirmModal(true);
    } finally {
      setIsCheckingCompaniesExport(false);
    }
  };

  // Execute the actual retry review
  const executeRetryReview = async () => {
    if (!exportId) return;

    setShowRetryConfirmModal(false);
    setIsRetryingReview(true);
    setError(null);
    // Reset step to show progress from the beginning
    setCurrentStep(ReviewStepKey.FETCHING_DATA);

    try {
      await eduleadV4.exports.retry({ id: exportId }, team);
      await exportContext?.refreshExport();
    } catch (err) {
      setError(v4Error(err).message || 'Failed to retry review');
      toastV4Error(err, 'Failed to retry review');
    } finally {
      setIsRetryingReview(false);
    }
  };

  // Format email validation provider name for display
  const formatProviderName = (provider: string): string => {
    const providerNames: Record<string, string> = {
      'ZEROBOUNCE': 'ZeroBounce',
      'NEVERBOUNCE': 'NeverBounce'
    };
    return providerNames[provider] || provider;
  };

  const visibleSteps = REVIEW_STEPS
    .filter(step => {
      if (step.key === ReviewStepKey.VALIDATING_EMAILS) return hasEmailValidation;
      if (step.key === ReviewStepKey.COMPARING_WITH_CRM) return isCrmTarget;
      return true;
    })
    .map(step => {
      if (step.key === ReviewStepKey.VALIDATING_EMAILS && emailValidationProvider) {
        return { ...step, label: `Validating emails with ${formatProviderName(emailValidationProvider)}` };
      }
      if (step.key === ReviewStepKey.COMPARING_WITH_CRM && exportTarget) {
        const crmName = exportTarget === 'SALESFORCE' ? 'Salesforce' : 'HubSpot';
        return { ...step, label: `Comparing data with your ${crmName} account` };
      }
      return step;
    });

  // Render progress UI
  const renderProgress = () => (
    <div className="export-step__section">
      <ReviewProgressSteps steps={visibleSteps} currentStep={currentStep} />
    </div>
  );

  // Handle decision change with optimistic update
  const handleDecisionChange = async (idSource: string, newDecision: string) => {
    if (!exportId || !rows) return;

    // Find current decision - check both parent and child records
    let currentRecord: ReviewRow | ReviewChildRecord | undefined = rows.find(r => r.id_source === idSource);
    let parentOfChild: ReviewRow | null = null;
    if (!currentRecord) {
      // Check child records
      for (const parent of rows) {
        const childRecord = parent.child_records?.find(c => c.id_source === idSource);
        if (childRecord) {
          currentRecord = childRecord;
          parentOfChild = parent;
          break;
        }
      }
    }

    // Prevent setting child to TAKE when parent is SKIP
    if (parentOfChild && newDecision === 'TAKE' && parentOfChild.user_decision === 'SKIP') {
      console.warn('Cannot set child to TAKE when parent is SKIP');
      return;
    }

    const oldDecision = currentRecord?.user_decision;

    // Optimistic update for takeCount (includes both parent and child institution records)
    // Also account for cascading SKIP to children when parent is set to SKIP
    const parentRecord = rows.find(r => r.id_source === idSource);
    const isParentRecord = !!parentRecord;

    if (newDecision === 'TAKE' && oldDecision !== 'TAKE') {
      setTakeCount(prev => prev + 1);
    } else if (newDecision === 'SKIP' && oldDecision === 'TAKE') {
      setTakeCount(prev => prev - 1);
    }

    // When parent is set to SKIP, also subtract children that were TAKE (they get cascaded to SKIP)
    if (isParentRecord && newDecision === 'SKIP') {
      const takeChildrenCount = parentRecord.child_records?.filter(c => c.user_decision === 'TAKE').length ?? 0;
      if (takeChildrenCount > 0) {
        setTakeCount(prev => prev - takeChildrenCount);
      }
    }

    // Optimistic update for both parent and child records
    setRows(prev => prev && prev.map(r => {
      // Check if this is the parent record
      if (r.id_source === idSource) {
        // Only cascade SKIP to children (not TAKE - user controls children individually)
        if (newDecision === 'SKIP' && r.child_records) {
          const updatedChildren = r.child_records.map(child => ({ ...child, user_decision: 'SKIP' }));
          return { ...r, user_decision: newDecision, child_records: updatedChildren };
        }
        return { ...r, user_decision: newDecision };
      }
      // Check if the idSource is in child_records
      if (r.child_records?.some(c => c.id_source === idSource)) {
        return {
          ...r,
          child_records: r.child_records.map(child =>
            child.id_source === idSource ? { ...child, user_decision: newDecision } : child
          )
        };
      }
      return r;
    }));

    // API call - backend automatically updates child associations; its after-image replaces the
    // optimistic row
    try {
      const { export_review_record: after } = await eduleadV4.exports.updateReviewRecord(
        { export_id: exportId, id: idSource, update_mask: 'decision', body: { decision: decisionOf(newDecision) } },
        team
      );
      if (after) {
        const row = reviewRowOf(after);
        setRows(prev => prev && prev.map(r => {
          if (r.id_source === idSource) return row;
          if (r.child_records?.some(c => c.id_source === idSource)) {
            return { ...r, child_records: r.child_records.map(c => (c.id_source === idSource ? row : c)) };
          }
          return r;
        }));
      }
      // Refresh unlocked institutions stats after decision change
      fetchUnlockedStats();
    } catch (error) {
      console.error('Failed to update decision:', error);
      // Revert by refetching current page
      await loadPage(pageTokens);
    }
  };

  // Handle record click to open explanation slideout
  const handleRecordClick = (record: ReviewRecord | ReviewChildRecord, recordType: string, parentIdCrm?: string) => {
    setSelectedRecord({ record, recordType, parentIdCrm });
  };

  // Render review grid with actual data
  const renderReviewGrid = () => {
    if (isLoadingRecords) {
      return (
        <div className="export-step__section export-review-gutter">
          <LoadingBars numberOfRows={5} />
        </div>
      );
    }

    if (!rows) {
      // Don't show error if we haven't attempted to load yet
      // (rows is null on initial render before useEffect runs)
      return null;
    }

    const records: ReviewRecord[] = rows;

    return (
      <div className="export-step__section">
        {/* Records Grid */}
        <ExportReviewGrid
          records={records}
          fieldMappings={fieldMappings}
          systemFieldMappings={systemFieldMappings}
          hasEmailValidation={!!hasEmailValidation}
          emailValidationProvider={emailValidationProvider}
          isCrmTarget={isCrmTarget}
          crmType={exportTarget === 'SALESFORCE' ? 'SALESFORCE' : 'HUBSPOT'}
          hubspotPortalId={exportContext?.hubspotPortalId}
          salesforceInstanceUrl={exportContext?.salesforceInstanceUrl}
          entityType={exportContext?.exportData?.entity as 'CONTACT' | 'INSTITUTION'}
          onDecisionChange={handleDecisionChange}
          onRecordClick={handleRecordClick}
          highlightedRecordIds={highlightedRecordIds}
          onRowClick={toggleRowHighlight}
          showAssociations={showAssociations}
          readOnly={showReadOnlyGrid}
          showControls={showControls}
          showChanges={showChanges}
        />
      </div>
    );
  };

  return (
    <>
      {/* Controls row - fixed width for usability */}
      {(isReady || showReadOnlyGrid) && (
        <div className="content content-1200">
          <Flex justifyContent="space-between" alignItems="center" style={{ marginBottom: '8px' }}>
            <Flex gap="8px">
              {isCrmTarget && exportContext?.exportData?.entity === 'CONTACT' && (
                <Button size="small" className="tiny" onClick={() => setShowAssociations(!showAssociations)}>
                  {showAssociations ? <BsEyeSlashFill style={{ fontSize: 14, marginRight: 6 }} /> : <BsEyeFill style={{ fontSize: 14, marginRight: 6 }} />}
                  Company Associations
                </Button>
              )}
              <Button size="small" className="tiny" onClick={() => setShowControls(!showControls)}>
                {showControls ? <BsEyeSlashFill style={{ fontSize: 14, marginRight: 6 }} /> : <BsEyeFill style={{ fontSize: 14, marginRight: 6 }} />}
                Actions
              </Button>
              {isCrmTarget && (
                <Button size="small" className="tiny" onClick={() => setShowChanges(!showChanges)}>
                  {showChanges ? <BsEyeSlashFill style={{ fontSize: 14, marginRight: 6 }} /> : <BsEyeFill style={{ fontSize: 14, marginRight: 6 }} />}
                  Changes
                </Button>
              )}
            </Flex>
          </Flex>
          {unlockedStats?.hasLimit && !exportContext?.readOnly && (
            <Flex alignItems="center" gap="8px" style={{ marginTop: '24px' }}>
              {unlockedStats.used + unlockedStats.wouldUnlock > unlockedStats.limit && (
                <BsExclamationTriangleFill style={{ color: 'var(--amplify-colors-yellow-60)', fontSize: '16px', flexShrink: 0 }} />
              )}
              <span style={{ fontSize: 'var(--amplify-font-sizes-small)', color: unlockedStats.used + unlockedStats.wouldUnlock > unlockedStats.limit ? 'var(--amplify-colors-yellow-60)' : 'var(--amplify-colors-font-tertiary)' }}>
                {unlockedStats.used + unlockedStats.wouldUnlock > unlockedStats.limit ? (
                  unlockedStats.limit - unlockedStats.used <= 0 ? (
                    <>This export requires unlocking {unlockedStats.wouldUnlock} new {unlockedStats.wouldUnlock === 1 ? 'account' : 'accounts'}, but you have none remaining. {unlockedStats.used}/{unlockedStats.limit} unlocked so far, <a href="/settings/service-limits" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'none'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'underline'}>see limits</a>.</>
                  ) : (
                    <>This export requires unlocking {unlockedStats.wouldUnlock} new {unlockedStats.wouldUnlock === 1 ? 'account' : 'accounts'}, but you only have {unlockedStats.limit - unlockedStats.used} remaining. {unlockedStats.used}/{unlockedStats.limit} unlocked so far, <a href="/settings/service-limits" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'none'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'underline'}>see limits</a>.</>
                  )
                ) : unlockedStats.alreadyUnlocked > 0 && unlockedStats.wouldUnlock === 0 ? (
                  <>All {unlockedStats.alreadyUnlocked} accounts in this export are already unlocked. {unlockedStats.used}/{unlockedStats.limit} unlocked so far, <a href="/settings/service-limits" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'none'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'underline'}>see limits</a>.</>
                ) : unlockedStats.alreadyUnlocked > 0 ? (
                  <>This export requires unlocking {unlockedStats.wouldUnlock} new {unlockedStats.wouldUnlock === 1 ? 'account' : 'accounts'}. {unlockedStats.alreadyUnlocked} already unlocked by prior exports, {unlockedStats.used}/{unlockedStats.limit} unlocked so far, <a href="/settings/service-limits" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'none'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'underline'}>see limits</a>.</>
                ) : (
                  <>This export requires unlocking {unlockedStats.wouldUnlock} new {unlockedStats.wouldUnlock === 1 ? 'account' : 'accounts'}. {unlockedStats.used}/{unlockedStats.limit} unlocked so far, <a href="/settings/service-limits" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'none'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'underline'}>see limits</a>.</>
                )}
              </span>
            </Flex>
          )}
          {/* Warning banner for emails not re-validated because the monthly limit was reached */}
          {hasEmailValidation && !isReviewInProgress && (exportContext?.exportData?.email_verification?.skipped_over_limit ?? 0) > 0 && (
            <div style={{ marginTop: '30px' }}>
              <Banner color="yellow">
                <span>
                  {exportContext?.exportData?.email_verification?.skipped_over_limit?.toLocaleString()} {exportContext?.exportData?.email_verification?.skipped_over_limit === 1 ? 'email was' : 'emails were'} not re-validated because your team reached its monthly email verification limit. They keep their existing validation status, <a href="/settings/service-limits" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>see limits</a>.
                </span>
              </Banner>
            </div>
          )}
          {/* Warning banner for missing companies in CRM */}
          {isCrmTarget && !isReviewInProgress && (exportContext?.exportData?.review?.missing_companies ?? 0) > 0 && (
            <div style={{ marginTop: '30px' }}>
              <Banner color={exportContext?.exportData?.companies_export_id ? 'blue' : 'yellow'}>
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  {!exportContext?.exportData?.companies_export_id && (
                    <span>
                      {(exportContext?.exportData?.review?.missing_companies ?? 0)} company {(exportContext?.exportData?.review?.missing_companies ?? 0) === 1 ? 'association' : 'associations'} cannot be exported because the {(exportContext?.exportData?.review?.missing_companies ?? 0) === 1 ? 'company doesn\'t' : 'companies don\'t'} exist in your {exportTarget === 'SALESFORCE' ? 'Salesforce' : 'HubSpot'}.
                    </span>
                  )}
                  {exportContext?.exportData?.companies_export_id ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '8px' }}>
                      <span>
                        Done with the{' '}
                        <a href={`/exports/${exportContext?.exportData?.companies_export_id}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                          companies export
                        </a>
                        ? Refresh this review to match them to your contacts.
                      </span>
                      <Button
                        size="small"
                        className="tiny blue"
                        onClick={handleRetryReviewClick}
                        isDisabled={isRetryingReview || isCheckingCompaniesExport}
                      >
                        Refresh
                      </Button>
                    </span>
                  ) : (
                    <Button
                      size="small"
                      className="tiny"
                      onClick={() => setShowCreateCompaniesModal(true)}
                      style={{ marginLeft: '16px', flexShrink: 0 }}
                    >
                      Create Companies
                    </Button>
                  )}
                </span>
              </Banner>
            </div>
          )}
        </div>
      )}

      {/* Progress steps - fixed width to align with stepper */}
      {isReviewInProgress && (
        <div className="content content-1200">
          <div className="export-step">
            {error && (
              <Banner color="yellow" style={{ marginTop: 0, marginBottom: '16px' }}>{error}</Banner>
            )}
            {renderProgress()}
          </div>
        </div>
      )}

      {/* The review grid */}
      {(isReady || showReadOnlyGrid || isFailed) && (
        <div className="content content-expanded export-review-fill">
          <div className="export-step">
            {error && !isReviewInProgress && (
              <Banner color="yellow" style={{ marginTop: 0, marginBottom: '16px' }}>{error}</Banner>
            )}
            {renderReviewGrid()}
          </div>
        </div>
      )}

      {/* Sticky bottom bar: pagination and export button (hidden in read-only mode) */}
      {isReady && !exportContext?.readOnly && (
        <div className="export-review-bottom-bar">
          <div className="content content-1200">
            <Flex justifyContent="space-between" alignItems="center">
              {rows && (pageTokens.length > 1 || nextPageToken) ? (
                <TokenPagination
                  page={pageTokens.length}
                  pageSize={pageSize}
                  shown={rows.length}
                  total={exportContext?.exportData?.review?.total}
                  hasNext={!!nextPageToken}
                  isLoading={isLoadingRecords}
                  onPrevious={() => loadPage(pageTokens.slice(0, -1))}
                  onNext={() => loadPage([...pageTokens, nextPageToken])}
                  inline
                />
              ) : (
                <div />
              )}
              <span
                data-tooltip-id={(takeCount === 0 || !canExecuteExport || (unlockedStats?.hasLimit && unlockedStats.used + unlockedStats.wouldUnlock > unlockedStats.limit)) ? 'main-tooltip' : undefined}
                data-tooltip-content={
                  !canExecuteExport
                    ? "You do not have permission to execute this export"
                    : (unlockedStats?.hasLimit && unlockedStats.used + unlockedStats.wouldUnlock > unlockedStats.limit)
                    ? "This export exceeds your account limit"
                    : (isCrmTarget ? "Select at least one contact to import" : "Select at least one contact to export")
                }
              >
                <Button
                  variation="primary"
                  size="small"
                  onClick={handleContinue}
                  isLoading={isStartingExport}
                  isDisabled={takeCount === 0 || isStartingExport || !canExecuteExport || (unlockedStats?.hasLimit && unlockedStats.used + unlockedStats.wouldUnlock > unlockedStats.limit)}
                  loadingText="Starting export..."
                >
                  {exportTarget === 'CSV' && `Export ${takeCount.toLocaleString()} ${takeCount === 1 ? 'record' : 'records'} to CSV`}
                  {exportTarget === 'JSON' && `Export ${takeCount.toLocaleString()} ${takeCount === 1 ? 'record' : 'records'} to JSON`}
                  {exportTarget === 'HUBSPOT' && `Import ${takeCount.toLocaleString()} ${takeCount === 1 ? 'record' : 'records'} to HubSpot`}
                  {exportTarget === 'SALESFORCE' && `Import ${takeCount.toLocaleString()} ${takeCount === 1 ? 'record' : 'records'} to Salesforce`}
                  <TbArrowRight style={{ marginLeft: 8 }} />
                </Button>
              </span>
            </Flex>
          </div>
        </div>
      )}

      {/* Record explanation slideout */}
      <RecordExplanationSlideout
        isOpen={!!selectedRecord}
        onClose={() => setSelectedRecord(null)}
        record={selectedRecord?.record ?? null}
        fieldMappings={fieldMappings}
        crmName={exportTarget === 'SALESFORCE' ? 'Salesforce' : 'HubSpot'}
        crmType={exportTarget === 'SALESFORCE' ? 'SALESFORCE' : 'HUBSPOT'}
        entityType={exportContext?.exportData?.entity as 'CONTACT' | 'INSTITUTION'}
        idCrm={selectedRecord?.recordType === 'CONTACT' || selectedRecord?.recordType === 'INSTITUTION' ? (selectedRecord?.record as ReviewRecord)?.id_crm : selectedRecord?.parentIdCrm}
        idCrmCompany={selectedRecord?.recordType === 'CONTACT_INSTITUTION_ASSOCIATION' ? (selectedRecord?.record?.data?.id_crm_company as string) : undefined}
        hubspotPortalId={exportContext?.hubspotPortalId}
      />

      {/* Create Companies Modal */}
      <CreateCompaniesModal
        isOpen={showCreateCompaniesModal}
        onClose={() => setShowCreateCompaniesModal(false)}
        onConfirm={async () => {
          const { export: created } = await eduleadV4.exports.create(
            { request_id: crypto.randomUUID(), export: { source_export_id: exportId } },
            team
          );
          await exportContext?.refreshExport();
          return { id: created?.id ?? '' };
        }}
        crmName={exportTarget === 'SALESFORCE' ? 'Salesforce' : 'HubSpot'}
      />

      {/* Retry Review Confirmation Modal */}
      <ConfirmModal
        isOpen={showRetryConfirmModal}
        onClose={() => setShowRetryConfirmModal(false)}
        onConfirm={executeRetryReview}
        title="Retry Review"
        message={<>The companies export hasn't completed yet.<br /><br />Are you sure you want to retry this review now? The new companies may not be available in your CRM.</>}
        confirmText="Retry Anyway"
        isLoading={isRetryingReview}
        loadingText="Retrying..."
        hideCancel
      />
    </>
  );
}
